/**
 * PlanGate.js — ProTrackNG Feature Access Control
 *
 * Exports:
 *   usePlanAccess(feature)          — hook, checks localStorage (fast, for nav icons)
 *   useServerPlanAccess(feature)    — hook, verifies with server on mount (for gated pages)
 *   <PlanGate feature="...">        — plan-tier gate component
 *   <CompanyGate>                   — requires company profile
 *   <CompanyBanner>                 — soft prompt (Dashboard)
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Building2, ArrowRight, Zap } from "lucide-react";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";

// ── Plan hierarchy ──────────────────────────────────────────────────
// trial === basic (2026-08-13 product decision): a company on an active
// 14-day trial gets exactly Basic-tier privileges, nothing more. This was
// previously 2 (Professional's level), which silently gave every trial
// user Professional-gated features (all_sources, ai_matching,
// deadline_pulse, win_probability, analytics, advanced_search) for the
// whole trial — not a deliberate freemium choice, just this constant
// never having been revisited since Professional was added.
export const PLAN_LEVELS = {
  trial:        1,
  basic:        1,
  professional: 2,
  enterprise:   3,
  elite:        4,
  super_admin:  99,
  admin:        99,
};

// ── Feature → minimum plan level ───────────────────────────────────
// ai_matching was 2 (Professional) — contradicted Billing.js's Basic
// copy ("Basic AI matching" is explicitly a Basic-tier line item) and
// the backend endpoint itself (GET /tenders/matched/for-me) has never
// had a tier gate. Fixed to 1. The real Basic-vs-Professional
// distinction is alert *volume* now (50 new matches/month, capped at
// the point Alert rows are created — see alert_service.py — vs
// unlimited for Professional+), not access to matching itself; "Basic
// AI matching" / "Advanced AI matching" in Billing.js's copy reads
// correctly against that.
export const FEATURE_REQUIREMENTS = {
  tenders_browse:      1,
  save_tenders:        1,
  email_alerts:        1,
  ai_matching:         1,
  all_sources:         2,
  whatsapp_alerts:     2,
  deadline_pulse:      2,
  win_probability:     2,
  advanced_search:     2,
  analytics:           2,
  compliance_hub:      3,
  hse_reports:         3,
  bid_generator:       3,
  market_intel:        3,
  regulatory_calendar: 3,
  multi_company:       3,
  competitor_intel:    4,
  auto_bid_assistant:  4,
};

const PLAN_META = {
  1: { name: "Basic",        color: "#4f8ef7", price: "₦15k" },
  2: { name: "Professional", color: "#00d4a1", price: "₦35k" },
  3: { name: "Enterprise",   color: "#ffd166", price: "₦75k" },
  4: { name: "Elite",        color: "#a78bfa", price: "₦160k" },
};

// ── Fast localStorage hook (used for sidebar icons, nav) ───────────
export function usePlanAccess(feature) {
  const raw  = localStorage.getItem("sub_status");
  const role = localStorage.getItem("user_role");

  if (role === "SUPER_ADMIN" || role === "ADMIN") return true;

  let plan = "basic";
  try { plan = JSON.parse(raw || "{}").plan || "basic"; } catch {}
  if (plan === "super_admin") return true;

  const level    = PLAN_LEVELS[plan?.toLowerCase()] || 0;
  const required = FEATURE_REQUIREMENTS[feature] || 0;
  return level >= required;
}

// ── Server-verified hook (used by gated page components) ───────────
// Calls /payments/status on mount so a user cannot bypass by editing localStorage.
// Returns { hasAccess: bool, loading: bool }
export function useServerPlanAccess(feature) {
  const role = localStorage.getItem("user_role");
  const isAdmin = role === "SUPER_ADMIN" || role === "ADMIN";

  // Hooks must always be called — no early return before them
  const [state, setState] = useState(
    isAdmin ? { hasAccess: true, loading: false } : { hasAccess: false, loading: true }
  );

  useEffect(() => {
    // Admins bypass — no server call needed
    if (isAdmin) return;

    const token = localStorage.getItem("access_token");
    if (!token) { setState({ hasAccess: false, loading: false }); return; }

    fetch(`${API}/payments/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        // Update localStorage so sidebar/icons stay in sync
        localStorage.setItem("sub_status", JSON.stringify(data));

        const plan     = data.plan || "basic";
        const isActive = data.status === "active" || data.status === "trial";
        if (plan === "super_admin") { setState({ hasAccess: true, loading: false }); return; }

        const level    = PLAN_LEVELS[plan?.toLowerCase()] || 0;
        const required = FEATURE_REQUIREMENTS[feature] || 0;
        setState({ hasAccess: isActive && level >= required, loading: false });
      })
      .catch(() => {
        // On network error fall back to localStorage to avoid locking out users
        const raw = localStorage.getItem("sub_status");
        let plan = "basic", active = false;
        try { const p = JSON.parse(raw || "{}"); plan = p.plan || "basic"; active = p.status === "active" || p.status === "trial"; } catch {}
        const level    = PLAN_LEVELS[plan?.toLowerCase()] || 0;
        const required = FEATURE_REQUIREMENTS[feature] || 0;
        setState({ hasAccess: active && level >= required, loading: false });
      });
  }, [feature, isAdmin]);

  return state;
}

// ── PlanGate component — wraps gated page content ──────────────────
// Uses server-side verification so localStorage manipulation is blocked.
export function PlanGate({ feature, children, inline = false }) {
  const { hasAccess, loading } = useServerPlanAccess(feature);

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "80px", color: "var(--text-muted)", gap: "10px" }}>
      <div style={{ width: "20px", height: "20px", border: "2px solid rgba(0,212,161,0.2)", borderTopColor: "var(--accent-green)", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <style>{"@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}"}</style>
      Verifying access...
    </div>
  );

  if (hasAccess) return children;

  const required = FEATURE_REQUIREMENTS[feature] || 2;
  const meta     = PLAN_META[required];

  if (inline) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "var(--text-muted)", fontSize: "12px" }}>
        <Lock size={12} />
        <span>{meta?.name} plan required</span>
      </div>
    );
  }

  return <LockedFeature requiredLevel={required} meta={meta} />;
}

function LockedFeature({ requiredLevel, meta }) {
  const navigate = useNavigate();
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "400px", padding: "48px 24px", textAlign: "center" }}>
      <div style={{ width: "72px", height: "72px", borderRadius: "20px", background: `${meta?.color}15`, border: `1px solid ${meta?.color}30`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "24px" }}>
        <Lock size={28} color={meta?.color} />
      </div>
      <div style={{ fontSize: "11px", fontWeight: "800", color: meta?.color, letterSpacing: "2px", textTransform: "uppercase", marginBottom: "12px" }}>
        {meta?.name} Plan Required
      </div>
      <h2 style={{ fontSize: "22px", fontWeight: "800", marginBottom: "12px", color: "var(--text-primary)" }}>
        This feature is locked
      </h2>
      <p style={{ fontSize: "14px", color: "var(--text-secondary)", maxWidth: "380px", lineHeight: "1.7", marginBottom: "32px" }}>
        Upgrade to the <strong style={{ color: meta?.color }}>{meta?.name} plan</strong> ({meta?.price}/month) to unlock this feature and everything below it.
      </p>
      <button
        onClick={() => navigate("/billing")}
        style={{ display: "flex", alignItems: "center", gap: "8px", padding: "12px 28px", background: `linear-gradient(135deg, ${meta?.color}, ${meta?.color}bb)`, border: "none", borderRadius: "10px", color: requiredLevel === 4 ? "#fff" : "#080d1a", fontSize: "14px", fontWeight: "700", cursor: "pointer" }}
        onMouseEnter={e => e.currentTarget.style.opacity = "0.88"}
        onMouseLeave={e => e.currentTarget.style.opacity = "1"}
      >
        <Zap size={15} /> Upgrade to {meta?.name} — {meta?.price}/mo <ArrowRight size={14} />
      </button>
      <button onClick={() => navigate("/billing")} style={{ marginTop: "12px", background: "none", border: "none", color: "var(--text-muted)", fontSize: "13px", cursor: "pointer" }}>
        View all plans →
      </button>
    </div>
  );
}

// ── CompanyGate — hard block if no company_id ──────────────────────
export function CompanyGate({ children, user }) {
  const navigate = useNavigate();
  if (user?.company_id)                                          return children;
  if (user?.role === "SUPER_ADMIN" || user?.role === "ADMIN")    return children;

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "400px", padding: "48px 24px", textAlign: "center" }}>
      <div style={{ width: "72px", height: "72px", borderRadius: "20px", background: "rgba(0,212,161,0.08)", border: "1px solid rgba(0,212,161,0.2)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "24px" }}>
        <Building2 size={28} color="var(--accent-green)" />
      </div>
      <div style={{ fontSize: "11px", fontWeight: "800", color: "var(--accent-green)", letterSpacing: "2px", textTransform: "uppercase", marginBottom: "12px" }}>
        Company Profile Required
      </div>
      <h2 style={{ fontSize: "22px", fontWeight: "800", marginBottom: "12px", color: "var(--text-primary)" }}>Set up your company first</h2>
      <p style={{ fontSize: "14px", color: "var(--text-secondary)", maxWidth: "400px", lineHeight: "1.7", marginBottom: "32px" }}>
        ProTrackNG uses your company profile to match tenders, calculate win probability, and personalise your intelligence feed. It takes 2 minutes.
      </p>
      <button
        onClick={() => navigate("/companies")}
        style={{ display: "flex", alignItems: "center", gap: "8px", padding: "12px 28px", background: "linear-gradient(135deg, #00d4a1, #00b888)", border: "none", borderRadius: "10px", color: "#080d1a", fontSize: "14px", fontWeight: "700", cursor: "pointer" }}
      >
        <Building2 size={15} /> Complete Company Profile <ArrowRight size={14} />
      </button>
    </div>
  );
}

// ── CompanyBanner — soft prompt (Dashboard, non-blocking) ──────────
export function CompanyBanner({ user }) {
  const navigate = useNavigate();
  if (user?.company_id || user?.role === "SUPER_ADMIN" || user?.role === "ADMIN") return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "14px", padding: "14px 20px", background: "rgba(0,212,161,0.06)", border: "1px solid rgba(0,212,161,0.2)", borderRadius: "12px", marginBottom: "24px", flexWrap: "wrap" }}>
      <Building2 size={18} color="var(--accent-green)" style={{ flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: "200px" }}>
        <div style={{ fontSize: "13px", fontWeight: "700", color: "var(--text-primary)", marginBottom: "2px" }}>Complete your company profile</div>
        <div style={{ fontSize: "12px", color: "var(--text-secondary)" }}>Register your company to unlock AI matching, win probability and personalised tender feeds.</div>
      </div>
      <button onClick={() => navigate("/companies")} style={{ padding: "8px 18px", background: "var(--accent-green)", border: "none", borderRadius: "8px", color: "#080d1a", fontSize: "13px", fontWeight: "700", cursor: "pointer", flexShrink: 0 }}>
        Set Up Now →
      </button>
    </div>
  );
}
